import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common'
import {
    provideHttpClient,
    withInterceptors,
    withJsonpSupport,
    withXsrfConfiguration,
    withXhr,
} from '@angular/common/http'
import {
    type ApplicationConfig,
    importProvidersFrom,
    inject,
    isDevMode,
    provideAppInitializer,
    provideZonelessChangeDetection,
} from '@angular/core'
import {
    NoPreloading,
    provideRouter,
    RouteReuseStrategy,
    withComponentInputBinding,
    withInMemoryScrolling,
    withPreloading,
    withRouterConfig,
} from '@angular/router'
import { AdminAuthStateService, AuthStateService } from '@baboor/baboor-auth'
import { appInitializerFn, BABOOR_ENVIRONMENT } from '@baboor/baboor-core'
import { LANGUAGE_SYNC_HANDLER } from '@baboor/baboor-language'
import { UserSettingsApiService } from '@baboor/baboor-user-setting'
import {
    ACCESS_TOKEN_KEY,
    AUTH_API_URL,
    AuthHeaderInterceptorFn,
    REFRESH_TOKEN_KEY,
    TokenInterceptorFn,
} from '@baboor/common-auth'
import { BaboorPreset } from '@baboor/prime-modules'
import { FullCalendarModule } from '@fullcalendar/angular'
import {
    IonicRouteStrategy,
    provideIonicAngular,
} from '@ionic/angular/standalone'
import { provideTransloco } from '@jsverse/transloco'
import { NgIconsModule, provideNgIconsConfig } from '@ng-icons/core'
import {
    heroArrowPath,
    heroBookOpen,
    heroChevronDown,
    heroChevronUp,
    heroClock,
    heroDocumentText,
    heroUser,
    heroUserPlus,
    heroUsers,
} from '@ng-icons/heroicons/outline'
// import { provideClientHead } from '@unhead/angular/client'
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api'
import { provideOptimus } from '@openng/optimus-ui/config'
import {
    DialogService,
    DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog'
import { environment } from '../environment/environment'
import { appRoutes } from './app.routes'
import { TranslocoHttpLoader } from './transloco-loader'

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: BABOOR_ENVIRONMENT, useValue: environment },
        { provide: AUTH_API_URL, useValue: environment.authApiUrl },
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        { provide: ACCESS_TOKEN_KEY, useValue: 'baboorAccessToken' },
        { provide: REFRESH_TOKEN_KEY, useValue: 'baboorRefreshToken' },
        {
            // The super-admin login page signs in through
            // AdminAuthStateService (it owns adminLogin()). Aliasing
            // AuthStateService to the same instance keeps the guards,
            // interceptors and app initializer on one shared session.
            provide: AdminAuthStateService,
            useClass: AdminAuthStateService,
        },
        {
            provide: AuthStateService,
            useExisting: AdminAuthStateService,
        },
        // provideClientHead(),
        provideAppInitializer(appInitializerFn),
        provideIonicAngular(),
        provideZonelessChangeDetection(),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({
                scrollPositionRestoration: 'enabled',
                anchorScrolling: 'enabled',
            }),
            withRouterConfig({ onSameUrlNavigation: 'reload' }),
            withComponentInputBinding(),
            withPreloading(NoPreloading),
        ),
        provideHttpClient(
            withXhr(),
            withXsrfConfiguration({}),
            withJsonpSupport(),
            withInterceptors([AuthHeaderInterceptorFn, TokenInterceptorFn]),
        ),

        importProvidersFrom(FullCalendarModule),
        {
            provide: DATE_PIPE_DEFAULT_OPTIONS,
            useValue: { dateFormat: 'shortDate' },
        },

        provideOptimus({
            theme: {
                preset: BaboorPreset,
                options: {
                    prefix: 'p',
                    darkModeSelector: '.dark',
                    cssLayer: {
                        name: 'optimus',
                        order: 'theme, base, optimus, utilities',
                    },
                },
            },
        }),

        provideNgIconsConfig({
            size: '1.5rem',
            color: 'currentColor',
        }),

        importProvidersFrom(
            NgIconsModule.withIcons({
                heroClock,
                heroDocumentText,
                heroUsers,
                heroChevronUp,
                heroChevronDown,
                heroArrowPath,
                heroUserPlus,
                heroBookOpen,
                heroUser,
            }),
        ),
        MessageService,
        ConfirmationService,
        DialogService,
        provideTransloco({
            config: {
                availableLangs: ['en', 'es', 'fa'],
                defaultLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        {
            provide: LANGUAGE_SYNC_HANDLER,
            useFactory: () => {
                const authState = inject(AuthStateService)
                const userSettingsApi = inject(UserSettingsApiService)
                return {
                    pushLanguage(code: string) {
                        if (authState.isLoggedIn()) {
                            const userId = authState.getUserId()
                            if (userId) {
                                userSettingsApi
                                    .updateUserSetting(userId, {
                                        preferredLanguage: code,
                                    })
                                    .subscribe()
                            }
                        }
                    },
                }
            },
        },
    ],
}
